import { decryptData, getSecretKey } from "@/features/cryptography";
import { store } from "@/store";
import { useCheckPortfolioProgressQuery } from "@/store/services/facades/endpoints/pea";
import { PortfolioView, supabaseApi, useGetActiveSummariesQuery } from "@/store/services/supabase";
import { hexToArrayBuffer } from "@/utils/crypto";
import { useKeys } from "@/utils/hooks/useKeys";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTimeout } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { notification } from "antd";
import { useEffect, useMemo, useState } from "react";
export type SummaryWithProgress = PortfolioView & {
  status: string;
  position: number | null;
};
export const useActiveSummariesWithProgress = () => {
  // Delay ensures tag invalidation (getPortfolios) works
  const [ready, setReady] = useState(false);
  const {
    start
  } = useTimeout(() => setReady(true), 3000);
  useEffect(() => {
    start();
  }, [start]);
  const {
    data: activeSummaries,
    isLoading: isLoadingSummaries,
    error: summariesError
  } = useGetActiveSummariesQuery(undefined, {
    skip: !ready
  });
  const [{
    user,
    org
  }] = useKeys();
  const [mergedSummaries, setMergedSummaries] = useState<SummaryWithProgress[]>();
  const summaryIds = useMemo(() => activeSummaries?.items.map(item => item.summary_id!).filter(Boolean) ?? [], [activeSummaries]);
  const {
    data: progress,
    isFetching: isFetchingProgress,
    error: progressError
  } = useCheckPortfolioProgressQuery(summaryIds, {
    skip: summaryIds.length === 0 || !ready
  });
  useEffect(() => {
    const decryptNames = async () => {
      if (!activeSummaries?.items || !progress) return;
      const results: SummaryWithProgress[] = await Promise.all(activeSummaries.items.map(async summary => {
        const match = progress.find(p => p.report_id === summary.summary_id);
        let name = "";
        try {
          const privateKey = summary.is_private ? user?.keyPair.privateKey : org?.keyPair?.privateKey;
          if (privateKey && summary.secret_enc && summary.name) {
            const secret = await getSecretKey(privateKey, summary.secret_enc);
            name = await decryptData(secret, hexToArrayBuffer(summary.name));
          }
        } catch {
          name = "[Encrypted Name]";
        }
        return {
          ...summary,
          name,
          status: match?.status ?? "pending",
          position: match?.position ?? null
        };
      }));
      setMergedSummaries(results.sort((a, b) => {
        if (a.position === null) return 1;
        if (b.position === null) return -1;
        return a.position - b.position;
      }));
    };
    decryptNames();
  }, [activeSummaries, progress, user, org]);
  useEffect(() => {
    if (!mergedSummaries) return;
    mergedSummaries.map(summary => {
      if (summary.status === "complete") {
        store.dispatch(supabaseApi.endpoints.updatePortfolioReport.initiate({
          id: summary.id!,
          summaryId: summary.summary_id,
          isGenerated: true
        }));
        notification.open({
          duration: 0,
          message: <div className="flex gap-2">
              <CheckCircleOutlined />
              <span>Summary Generated</span>
            </div>,
          description: <div className="text-sm">
              Your summary &quot;<em>{summary.name}</em>&quot; is ready to download.
            </div>
        });
      }
      if (summary.status === "error" || summary.status === "unknown") {
        store.dispatch(supabaseApi.endpoints.updatePortfolioReport.initiate({
          id: summary.id!,
          summaryId: null,
          isGenerated: false
        }));
        notification.open({
          duration: 0,
          message: <div className="flex gap-2 font-semibold text-red-500">
              <CloseCircleOutlined />
              <span>Summary Failed</span>
            </div>,
          description: <div className="text-sm">
              {summary.status === "error" ? <>
                  There was a problem generating the summary &quot;<em>{summary.name}</em>&quot;. Please try again. If
                  the issue persists, contact support.
                </> : <>
                  Unknown error while generating the summary &quot;<em>{summary.name}</em>&quot;. Please try again. If
                  the issue persists, contact support.
                </>}
            </div>
        });
        Sentry.captureException("Summary Generation Error", {
          tags: {
            section: "Summary Generation"
          },
          extra: {
            userId: summary.user_id,
            portfolioId: summary.id,
            groupId: summary.group_id,
            summaryId: summary.summary_id,
            summaryStatus: summary.status
          }
        });
      }
    });
  }, [mergedSummaries]);
  return {
    summaries: mergedSummaries,
    isLoading: isLoadingSummaries || isFetchingProgress,
    error: summariesError || progressError
  };
};